<template>
  <section class="view-container" style="overflow:hidden;">
    <portal :to="headerPortal" :disabled="headerPortal === ''">
      <h1 class="title is-size-5">{{ currentDealer.name }}</h1>
    </portal>
    <b-tabs class="roll-out-tabs" type="is-toggle" v-model="tabSelected" expanded :animated="false">
      <b-tab-item label="Directory" class="directory-container" active>
        <company-directory-component
          :key="'company-' + directoryInstanceKey"
          :inFocus="companyDirectoryInFocus">
        </company-directory-component>
      </b-tab-item>
      <b-tab-item label="Customers" class="directory-container">
        <customer-directory-component
          :key="'customer-' + directoryInstanceKey"
          :inFocus="cusomterDirectoryInFocus">
        </customer-directory-component>
      </b-tab-item>
    </b-tabs>
    <portal :to="actionPortal">
      <div class="field is-grouped">
        <p class="field">
          <a class="button is-danger" @click="onClose">
            Close
          </a>
        </p>
      </div>
    </portal>
  </section>
</template>

<script>
import eventBus from '@/eventBus'
import utilities from '@/mixins/generic/utilities'

import { createNamespacedHelpers } from 'vuex'
import { VTooltip, VClosePopover } from 'v-tooltip'
import CustomerDirectoryComponent from '@/components/generic/CustomerDirectoryComponent'
import CompanyDirectoryComponent from '@/components/generic/CompanyDirectoryComponent'

const mapDealerState = createNamespacedHelpers('dealer').mapState

export default {
  name: 'CustomerRollOut',
  props: {
    actionPortal: {
      type: String,
      default: 'qv-footer-toolbar-left'
    },
    headerPortal: {
      type: String,
      default: 'qv-header'
    },
    defaultTabIndex: {
      type: Number,
      default: 0
    }
  },
  mixins: [utilities],
  directives: { tooltip: VTooltip, 'close-popover': VClosePopover },
  components: {
    'customer-directory-component': CustomerDirectoryComponent,
    'company-directory-component': CompanyDirectoryComponent
  },
  data () {
    return {
      tabSelected: 0,
      instanceId: this.$uuid.v4(),
      cusomterDirectoryInFocus: false,
      companyDirectoryInFocus: true,
      //Bumped by the quick view's refresh button to rebuild both directories from scratch.
      directoryInstanceKey: 0
    }
  },
  computed: {
    ...mapDealerState(['currentDealer'])
  },
  methods: {
    onClose: function () {
      eventBus.$emit('close-global-quick-view', {})
    },
    //The refresh icon in the quick view header only broadcasts quickview-refresh - each view has to
    //answer it, and this one never did, so the button did nothing here. A new key re-mounts both
    //directories: search, selections, magnifier and panel state all start over, recent contacts are
    //fetched again, and mounted() puts the caret back in the active tab's search box. The selected
    //tab is kept.
    onQuickviewRefresh: function () {
      this.directoryInstanceKey++
    },
    setFocusByTab: function (tabIndex) {
      if (tabIndex === 0) {
        this.companyDirectoryInFocus = true
        this.cusomterDirectoryInFocus = false
      } else {
        this.companyDirectoryInFocus = false
        //Was `this.customerDirectoryComponent = true` - a typo for the prop below, so it set a stray
        //field and the customer tab's search box never took focus on switching to it.
        this.cusomterDirectoryInFocus = true
      }
    },
    //Alt+1 / Alt+2 move between the two tabs without leaving the keyboard - Garrett, 2026-09-16:
    //"anything that can make me move to customer, and then keep my cursor. The problem is switching
    //between mouse and keyboard is very cumbersome". Alt rather than Ctrl because Ctrl+1 is already
    //what opens this panel, and the digits match the tab order left to right. The focus watcher on
    //each tab puts the caret in that tab's search box, so typing continues straight away.
    onDirectoryShortcut: function (event) {
      if (!event.altKey || event.ctrlKey || event.metaKey) {
        return
      }
      if (event.key === '1' && this.tabSelected !== 0) {
        event.preventDefault()
        this.tabSelected = 0
      } else if (event.key === '2' && this.tabSelected !== 1) {
        event.preventDefault()
        this.tabSelected = 1
      }
    },
    //Tab and Shift+Tab cycle only through this directory while it is open - Craig, 2026-09-18: "it
    //should only tab between things within the customer/company directory". The search boxes carry
    //positive tabindexes (3, 4, 5), which the browser orders against the WHOLE page, so Tab otherwise
    //walked out into the dashboard behind the quick view.
    onDirectoryTab: function (event) {
      if (event.key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) {
        return
      }
      let stops = this.getDirectoryTabStops()
      if (!stops.length) {
        return
      }
      event.preventDefault()
      let current = stops.indexOf(document.activeElement)
      let next = 0
      if (current === -1) {
        next = event.shiftKey ? stops.length - 1 : 0
      } else {
        next = (current + (event.shiftKey ? -1 : 1) + stops.length) % stops.length
      }
      stops[next].focus()
    },
    //What the browser itself would stop on, limited to what can actually be seen: not the inactive tab
    //(display:none), not a rolled-up panel (.header-collapsed only CLIPS its contents, they stay
    //focusable), not the magnifier's inert copy, and not the zero-size validation inputs the button
    //selectors hide. Ordered the browser's way - positive tabindex ascending, then 0 in document order.
    getDirectoryTabStops: function () {
      let stops = Array.from(this.$el.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')).filter((element) => {
        if (element.disabled || element.tabIndex < 0 || element.closest('[inert], .header-collapsed')) {
          return false
        }
        let box = element.getBoundingClientRect()
        return box.width > 0 && box.height > 0 && window.getComputedStyle(element).visibility !== 'hidden'
      })
      let ranked = stops.filter((element) => element.tabIndex > 0).sort((a, b) => a.tabIndex - b.tabIndex)
      return ranked.concat(stops.filter((element) => element.tabIndex === 0))
    }
  },
  watch: {
    tabSelected: function (value) {
      this.setFocusByTab(value)
    }
  },
  mounted: function () {
    if (this.defaultTabIndex) {
      this.tabSelected = this.defaultTabIndex
    }

    this.setFocusByTab(this.tabSelected)
    document.addEventListener('keydown', this.onDirectoryShortcut)
    document.addEventListener('keydown', this.onDirectoryTab)
    eventBus.$on('quickview-refresh', this.onQuickviewRefresh)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onDirectoryShortcut)
    document.removeEventListener('keydown', this.onDirectoryTab)
    eventBus.$off('quickview-refresh', this.onQuickviewRefresh)
  }
}
</script>

<style scoped>
  .directory-container {
    height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  /* The gap between the tabs and the search box was an empty LINE, not a margin: main.scss sets
     `.b-tabs .tab-content { display: unset }`, which makes it inline, and an inline box wrapping the
     directory's block content opens with a blank line box a full line-height tall. Measured in the
     browser: tabs end at 129px, the search box started at 153px. Block with no padding (Buefy's own
     1rem) puts the box directly under the tabs. Bulma's 1.5rem margin under .tabs goes as well.
     The global tab style in main.scss also pads each li around the link and adds a separate green
     underline, which left the green block flush with the top of the strip and grey plus an underline
     below it, so it read as cut off - the link now fills the li. Child combinators keep all of this
     off any tabs nested inside the directories; li.is-active is listed because main.scss's is-active
     rule outranks plain li. */
  .roll-out-tabs ::v-deep > nav.tabs {
    margin-bottom: 0;
  }

  .roll-out-tabs ::v-deep > .tab-content {
    display: block;
    padding: 0;
  }

  .roll-out-tabs ::v-deep > nav.tabs li,
  .roll-out-tabs ::v-deep > nav.tabs li.is-active {
    padding: 0;
    border-bottom: 0;
  }

  .roll-out-tabs ::v-deep > nav.tabs li a {
    padding: 0.6em 0.5em;
    border-radius: 4px 4px 0 0;
  }
</style>
